import React, { useState, useEffect } from "react";
import { Form, Input, Typography, Spin, Button, Checkbox } from "antd";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeftOutlined, ClockCircleOutlined, UserOutlined } from "@ant-design/icons";
import AuthServ from "../service/AuthServ";
import Navbar from "./Navbar";
import { jwtDecode } from "jwt-decode";
import Notify from "../util/Notify";
import SellerAuthVisualHero from "../component/SellerAuthVisualHero";

const { Paragraph } = Typography;

const SellerLogin = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // UI state
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Identifier, 2: Password, 3: Registration/Application
  const [identifier, setIdentifier] = useState("");
  const [, setIsExistingUser] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isNewUser, setIsNewUser] = useState(true);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  // If already logged in as a typical 'user', check their latest role from the backend and transition appropriately
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const checkLatestProfile = async () => {
      setLoading(true);
      try {
        const response = await AuthServ.single(true);
        const u = response?.data || response;
        if (u) {
          const latestRole = (u.role || "").toLowerCase();
          
          // Update local storage with latest user data and role
          localStorage.setItem("user", JSON.stringify(u));
          localStorage.setItem("role", latestRole);
          window.dispatchEvent(new Event("auth-change"));

          if (latestRole === "seller") {
            navigate("/seller/dashboard");
            return;
          }
          if (latestRole === "pendingseller") {
            navigate("/pending-seller");
            return;
          }

          // If they are still a regular 'user', load their details into step 3
          setUserData(u);
          setIsExistingUser(true);
          setIsNewUser(false);
          setIdentifier(u.email || u.phone || "");
          setStep(3);
          form.setFieldsValue({
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            phone: u.phone
          });
        }
      } catch (e) {
        console.error("Failed to check latest profile on mount:", e);
      } finally {
        setLoading(false);
      }
    };

    checkLatestProfile();
  }, [form, navigate]);

  // Step 1: Check if user exists
  const handleContinue = async (values) => {
    const val = values.identifier.trim();
    if (!val) return;

    // Validation for Email or 10-digit mobile
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    const isMobile = /^\d{10}$/.test(val);

    if (!isEmail && !isMobile) {
      Notify.error(null, "Please enter a valid email or 10-digit mobile number.");
      return;
    }

    setLoading(true);
    try {
      const res = await AuthServ.checkUser(val);
      setIdentifier(val);
      if (res?.data?.exists) {
        const user = res.data;
        const role = user.role?.toLowerCase();

        setIsExistingUser(true);
        setIsNewUser(false);
        setUserData(user);

        // Pre-fill form fields for Step 3
        form.setFieldsValue({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone
        });

        // IF role is 'user', skip password and go straight to request form
        if (role === "user") {
          setStep(3);
          Notify.success("Account Found", "Please complete your shop details to become a seller.");
        } else if (role === "pendingseller") {
          setStep(2);
          Notify.warning("Application Pending", "Your seller request is still under review.");
        } else {
          setStep(2);
          Notify.info("Welcome Back", "Please enter your password to continue.");
        }
      } else {
        setIsExistingUser(false);
        setIsNewUser(true);
        setUserData(null);
        setStep(3);
        // Pre-fill identifier in the registration form
        if (isEmail) form.setFieldsValue({ email: val, firstName: "", lastName: "", phone: "" });
        else if (isMobile) form.setFieldsValue({ phone: val, firstName: "", lastName: "", email: "" });

        Notify.info("New Seller", "Join the Nearzon marketplace! Please fill in your details.");
      }
    } catch (error) {
      console.error("Check User Error:", error);
      const message = error.message || "Something went wrong. Please try again.";
      Notify.error(null, message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Login Existing User
  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await AuthServ.login(identifier, values.password);
      const token = response?.data?.token || response?.token;
      const user = response?.data?.user;

      if (!token) throw new Error("Authentication failed: No token received.");

      const decoded = jwtDecode(token);
      const role = (decoded.role || user?.role || "").toString().toLowerCase();

      // CRITICAL: Check account status BEFORE saving session
      if (user?.isActive === false || user?.isBlocked === true) {
        Notify.error("Access Denied", "Your account is currently inactive or blocked. Please contact support.");
        setLoading(false);
        return;
      }

      // Save auth info
      localStorage.setItem("token", token);
      localStorage.setItem("customerToken", token);
      localStorage.setItem("role", role);
      localStorage.setItem("user", JSON.stringify(user));
      window.dispatchEvent(new Event("auth-change"));

      // Check role flow
      if (role === "seller" || role === "pendingseller") {
        Notify.success("Login Successful", "Redirecting to your dashboard...");
        setTimeout(() => {
          if (role === "pendingseller") navigate("/pending-seller");
          else navigate("/seller/dashboard");
        }, 1000);
      } else {
        // Logged in as regular user, but needs to apply for seller
        setUserData(user);
        setIsExistingUser(true);
        setIsNewUser(false);
        setStep(3);
        form.setFieldsValue({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone || (/\d{10}/.test(identifier) ? identifier : "")
        });
        Notify.info("Become a Seller", "Complete the shop details to submit your request.");
      }
    } catch (error) {
      console.error("Login Error:", error);
      const message = error.message || "Invalid password. Please try again.";
      Notify.error(null, message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Register New User or Apply as Existing User
  const handleFinalSubmit = async (values) => {
    setLoading(true);
    try {
      let payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        shopName: values.shopName,
        shopDescription: values.shopDescription,
        agreement: values.agreement,
      };

      if (isNewUser) {
        payload.password = values.password;
      }

      await AuthServ.registerSeller(payload);

      if (!isNewUser) {
        localStorage.setItem("role", "pendingseller");
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        currentUser.role = "pendingseller";
        localStorage.setItem("user", JSON.stringify(currentUser));
        window.dispatchEvent(new Event("auth-change"));
      }

      Notify.success(
        "Request Submitted",
        isNewUser
          ? "Welcome! Your seller account is created and pending approval."
          : "Your seller application has been submitted successfully."
      );

      // Show success confirmation screen
      setRequestSubmitted(true);

    } catch (error) {
      console.error("Final Submission Error:", error);
      Notify.error(error, "Submission failed. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3 && isNewUser) setStep(1);
    else if (step === 3 && !isNewUser) setStep(2);
  };

  return (
    <>
      <Navbar />
      <div style={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        padding: '40px 20px'
      }}>
      <div className="nz-auth-split-container">
          <SellerAuthVisualHero activeStep={step} />

          <div className="nz-auth-form-side">
              <div className="nz-split-form-wrapper">
                  <h2>
                      {requestSubmitted ? "Request Sent!" : step === 1 ? "Seller Portal" : step === 2 ? "Welcome Back" : "Start Selling"}
                  </h2>
                  <p className="nz-split-subtitle">
                      {requestSubmitted
                        ? "Your seller application has been received successfully."
                        : step === 1
                          ? "Enter your email or mobile to continue"
                          : step === 2
                            ? userData?.role?.toLowerCase() === 'pendingseller'
                              ? <span>Application status for <b style={{ color: '#0B1F4D' }}>{identifier}</b></span>
                              : <span>Login for <b style={{ color: '#0B1F4D' }}>{identifier}</b></span>
                            : "Complete your seller registration"}
                  </p>

                  <Spin spinning={loading}>
                    {requestSubmitted ? (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <div style={{ marginBottom: 24 }}>
                            <Paragraph style={{ fontSize: 16, color: '#666' }}>
                              Thank you for your interest! Our team will review your application and notify you within 24-48 hours.
                            </Paragraph>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <Button
                              type="primary"
                              block
                              onClick={() => navigate("/")}
                              className="nz-split-submit-btn"
                            >
                              Return to Home
                            </Button>
                            <Button
                              type="link"
                              block
                              onClick={() => {
                                setRequestSubmitted(false);
                                setStep(1);
                                setIdentifier("");
                                setUserData(null);
                                setIsNewUser(true);
                                setIsExistingUser(false);
                                form.resetFields();
                              }}
                              style={{ color: '#0B1F4D', fontWeight: 600 }}
                            >
                              Login with another user account
                            </Button>
                          </div>
                        </div>
                    ) : (
                      <>
                        {step === 1 && (
                          <Form form={form} layout="vertical" onFinish={handleContinue} requiredMark={false} size="large">
                            <Form.Item
                              label="Email or Mobile"
                              name="identifier"
                              className="nz-premium-form-item"
                              rules={[{ required: true, message: "Email or Mobile is required" }]}
                            >
                              <Input
                                placeholder="Email or 10-digit mobile"
                                suffix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d+$/.test(val) && val.length > 10) {
                                    form.setFieldsValue({ identifier: val.slice(0, 10) });
                                  }
                                }}
                              />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                              <Button
                                type="primary"
                                htmlType="submit"
                                block
                                className="nz-split-submit-btn"
                              >
                                Continue
                              </Button>
                            </Form.Item>
                            
                            <div className="nz-split-footer" style={{ marginTop: '24px' }}>
                              New to Nearzon?{" "}
                              <Link to="/start-selling" className="nz-split-footer-link">
                                Learn more
                              </Link>
                            </div>
                          </Form>
                        )}

                        {step === 2 && (
                          userData?.role?.toLowerCase() === 'pendingseller' ? (
                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                              <div style={{
                                background: '#fff7ed',
                                padding: '24px',
                                borderRadius: '16px',
                                border: '1px solid #ffedd5',
                                marginBottom: '24px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                              }}>
                                <ClockCircleOutlined style={{ fontSize: 54, color: '#f97316', marginBottom: 20 }} />
                                <h3 style={{ color: '#9a3412', margin: '0 0 12px 0', fontWeight: 700 }}>Under Review</h3>
                                <p style={{ color: '#c2410c', margin: 0, fontSize: 16, lineHeight: '1.6' }}>
                                  Your seller application is currently being processed by our team.
                                  We will notify you via email as soon as your account is approved.
                                </p>
                              </div>
                              <Button
                                type="link"
                                block
                                onClick={handleBack}
                                style={{ color: '#bfbfbf', fontWeight: 500 }}
                                icon={<ArrowLeftOutlined />}
                              >
                                Use a different account
                              </Button>
                            </div>
                          ) : (
                            <Form layout="vertical" onFinish={handleLogin} requiredMark={false} size="large">
                              <Form.Item
                                label="Password"
                                name="password"
                                className="nz-premium-form-item"
                                rules={[{ required: true, message: "Password is required" }]}
                              >
                                <Input.Password
                                  placeholder="Enter your password"
                                />
                              </Form.Item>
                              
                                <div className="nz-form-meta-row nz-form-meta-row--right">
                                  <Link to="/forgot-password" className="nz-recovery-link">
                                    Recovery Password
                                  </Link>
                                </div>

                              <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                  block
                                  className="nz-split-submit-btn"
                                >
                                  Login
                                </Button>
                              </Form.Item>
                              
                              <Button
                                type="link"
                                block
                                onClick={handleBack}
                                style={{ marginTop: 12, color: '#bfbfbf' }}
                                icon={<ArrowLeftOutlined />}
                              >
                                Use a different account
                              </Button>
                            </Form>
                          )
                        )}

                        {step === 3 && (
                          <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleFinalSubmit}
                            requiredMark={false}
                            size="large"
                            initialValues={userData || {}}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                              <Form.Item
                                label="First Name"
                                name="firstName"
                                className="nz-premium-form-item"
                                rules={[{ required: true, message: "Required" }]}
                              >
                                <Input placeholder="John" disabled={!isNewUser} />
                              </Form.Item>
                              <Form.Item
                                label="Last Name"
                                name="lastName"
                                className="nz-premium-form-item"
                                rules={[{ required: true, message: "Required" }]}
                              >
                                <Input placeholder="Doe" disabled={!isNewUser} />
                              </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                              <Form.Item
                                label="Email Address"
                                name="email"
                                className="nz-premium-form-item"
                                rules={[{ required: true, type: 'email', message: 'Valid email required' }]}
                              >
                                <Input
                                  placeholder="John@example.com"
                                  disabled={!isNewUser && userData?.email}
                                  onBlur={async (e) => {
                                    const val = e.target.value?.trim();
                                    if (!val || !isNewUser) return;
                                    try {
                                      const res = await AuthServ.checkUser(val);
                                      if (res?.data?.exists) {
                                        const u = res.data;
                                        setIsExistingUser(true);
                                        setIsNewUser(false);
                                        setUserData(u);
                                        form.setFieldsValue({
                                          firstName: u.firstName,
                                          lastName: u.lastName,
                                          phone: u.phone
                                        });
                                        Notify.info("User Found", "Existing account details prefilled.");
                                      }
                                    } catch (err) { }
                                  }}
                                />
                              </Form.Item>
                              <Form.Item
                                label="Phone Number"
                                name="phone"
                                className="nz-premium-form-item"
                                rules={[
                                  { required: true, message: "Required" },
                                  { pattern: /^\d{10}$/, message: "10-digit number" }
                                ]}
                              >
                                <Input
                                  placeholder="1234567890"
                                  maxLength={10}
                                  disabled={!isNewUser && userData?.phone}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    form.setFieldsValue({ phone: val });
                                  }}
                                  onBlur={async (e) => {
                                    const val = e.target.value?.trim();
                                    if (!val || !isNewUser || !/^\d{10}$/.test(val)) return;
                                    try {
                                      const res = await AuthServ.checkUser(val);
                                      if (res?.data?.exists) {
                                        const u = res.data;
                                        setIsExistingUser(true);
                                        setIsNewUser(false);
                                        setUserData(u);
                                        form.setFieldsValue({
                                          firstName: u.firstName,
                                          lastName: u.lastName,
                                          email: u.email
                                        });
                                        Notify.info("User Found", "Existing account details prefilled.");
                                      }
                                    } catch (err) { }
                                  }}
                                />
                              </Form.Item>
                            </div>

                            {isNewUser && (
                              <Form.Item
                                label="Create Password"
                                name="password"
                                className="nz-premium-form-item"
                                rules={[
                                  { required: true, message: "Password required" },
                                  { min: 6, message: "Min 6 chars" }
                                ]}
                              >
                                <Input.Password placeholder="Min. 6 characters" />
                              </Form.Item>
                            )}

                            <Form.Item
                              name="agreement"
                              valuePropName="checked"
                              rules={[
                                  {
                                      validator: (_, value) =>
                                          value ? Promise.resolve() : Promise.reject(new Error('Please read and agree to the terms')),
                                  },
                              ]}
                            >
                                <Checkbox className="nz-checkbox-text" onChange={(e) => setAgreementChecked(e.target.checked)}>
                                    I agree to the <Link to="/terms-and-conditions" className="nz-recovery-link">Terms & Conditions</Link>
                                </Checkbox>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                              <Button
                                type="primary"
                                htmlType="submit"
                                block
                                className="nz-split-submit-btn"
                                disabled={!agreementChecked}
                              >
                                Submit Seller Application
                              </Button>
                            </Form.Item>
                            
                            <Button
                              type="link"
                              block
                              onClick={handleBack}
                              style={{ marginTop: 12, color: '#bfbfbf' }}
                              icon={<ArrowLeftOutlined />}
                            >
                              Go Back
                            </Button>
                          </Form>
                        )}
                      </>
                    )}
                  </Spin>
              </div>
          </div>
      </div>
      </div>
    </>
  );
};

export default SellerLogin;
